// ─────────────────────────────────────────────────────────────────────────────
// Distinctions d'équipe — calcul pur (sans Firebase) à partir des données déjà
// chargées par la page Équipes (pulses + objectifs). Aucune nouvelle collection,
// aucune attribution manuelle : les badges sont recalculés à chaque render.
//
// Chaque distinction désigne au plus une équipe gagnante (départage déterministe
// par métrique puis nom). `winner: null` si aucune équipe n'est éligible.
// La page mappe `key` → icône lucide pour l'affichage (garde ce module testable
// sans dépendance UI).
// ─────────────────────────────────────────────────────────────────────────────

export type BadgePulse = {
  period: string // 'YYYY-MM'
  mood_score: number
  challenges: string | null
  positives: string | null
  support_needed: string | null
  custom_answers?: Record<string, { prompt: string; answer: string | null }>
  // Longueur du narratif DÉJÀ calculée par le serveur. Quand elle est fournie,
  // elle remplace le calcul local — les champs texte ci-dessus peuvent alors
  // rester null.
  //
  // Pourquoi : le badge `communication` note le volume narratif de TOUTES les
  // équipes, or M-208 réserve ce narratif à l'admin ou au directeur de chaque
  // équipe, et /equipes est ouvert à tout le management (manager inclus). Envoyer
  // le texte fuiterait ; n'envoyer que le narratif autorisé donnerait un palmarès
  // différent selon le viewer, donc faux. Le serveur envoie la longueur : le
  // classement est identique pour tous et ne révèle rien.
  narrative_length?: number
}

export type BadgeTeamInput = {
  teamId: string
  teamName: string
  pulses: BadgePulse[] // tous les pulses connus de l'équipe
  objectivesDone: number
}

export type BadgeKey =
  | 'assiduity'
  | 'communication'
  | 'objectives'
  | 'climate'

export type TeamBadge = {
  key: BadgeKey
  label: string
  description: string
  winner: { teamId: string; teamName: string } | null
  metricLabel: string | null
}

const DEFAULT_WINDOW_MONTHS = 6

// Énumère les N dernières périodes 'YYYY-MM' en incluant `current`, du plus
// récent au plus ancien. Pur (pas de Date.now).
function windowPeriods(current: string, months: number): string[] {
  const [y, m] = current.split('-').map((n) => Number.parseInt(n, 10))
  if (!Number.isFinite(y) || !Number.isFinite(m)) return [current]
  const out: string[] = []
  let year = y
  let month = m
  for (let i = 0; i < months; i++) {
    out.push(`${year}-${String(month).padStart(2, '0')}`)
    month -= 1
    if (month === 0) {
      month = 12
      year -= 1
    }
  }
  return out
}

function narrativeLength(p: BadgePulse): number {
  // Longueur pré-calculée (serveur) prioritaire : voir BadgePulse.narrative_length.
  if (p.narrative_length !== undefined) return p.narrative_length
  let len = 0
  for (const t of [p.challenges, p.positives, p.support_needed]) {
    if (t) len += t.trim().length
  }
  if (p.custom_answers) {
    for (const a of Object.values(p.custom_answers)) {
      if (a.answer) len += a.answer.trim().length
    }
  }
  return len
}

// Sélectionne le gagnant : score le plus élevé, puis départage par `tiebreak`
// (plus élevé d'abord), puis par nom (ordre alphabétique stable). Ignore les
// équipes dont le score est <= 0.
function pickWinner(
  scored: Array<{ teamId: string; teamName: string; score: number; tiebreak: number }>,
): { teamId: string; teamName: string } | null {
  const eligible = scored.filter((s) => s.score > 0)
  if (eligible.length === 0) return null
  eligible.sort(
    (a, b) =>
      b.score - a.score ||
      b.tiebreak - a.tiebreak ||
      a.teamName.localeCompare(b.teamName),
  )
  const w = eligible[0]
  return { teamId: w.teamId, teamName: w.teamName }
}

export function computeTeamBadges(
  teams: BadgeTeamInput[],
  opts: { currentPeriod: string; windowMonths?: number },
): TeamBadge[] {
  const window = new Set(
    windowPeriods(opts.currentPeriod, opts.windowMonths ?? DEFAULT_WINDOW_MONTHS),
  )
  const windowSize = window.size

  const recentByTeam = new Map<string, BadgePulse[]>()
  for (const t of teams) {
    recentByTeam.set(
      t.teamId,
      t.pulses.filter((p) => window.has(p.period)),
    )
  }

  // 1. Les plus assidus — meilleure couverture de pulses sur la fenêtre.
  const assiduity = pickWinner(
    teams.map((t) => {
      const periods = new Set(recentByTeam.get(t.teamId)!.map((p) => p.period))
      return {
        teamId: t.teamId,
        teamName: t.teamName,
        score: periods.size,
        tiebreak: t.pulses.length,
      }
    }),
  )
  const assiduityMetric = assiduity
    ? (() => {
        const t = teams.find((x) => x.teamId === assiduity.teamId)!
        const covered = new Set(
          recentByTeam.get(t.teamId)!.map((p) => p.period),
        ).size
        return `${covered}/${windowSize} mois`
      })()
    : null

  // 2. Meilleurs communicateurs — volume narratif total sur la fenêtre.
  const communication = pickWinner(
    teams.map((t) => {
      const recent = recentByTeam.get(t.teamId)!
      return {
        teamId: t.teamId,
        teamName: t.teamName,
        score: recent.reduce((sum, p) => sum + narrativeLength(p), 0),
        tiebreak: recent.filter((p) => narrativeLength(p) > 0).length,
      }
    }),
  )
  const communicationMetric = communication
    ? (() => {
        const recent = recentByTeam.get(communication.teamId)!
        const detailed = recent.filter((p) => narrativeLength(p) > 0).length
        return `${detailed} partage${detailed > 1 ? 's' : ''} détaillé${detailed > 1 ? 's' : ''}`
      })()
    : null

  // 3. Champions des objectifs — plus d'objectifs terminés cette année.
  const objectives = pickWinner(
    teams.map((t) => ({
      teamId: t.teamId,
      teamName: t.teamName,
      score: t.objectivesDone,
      tiebreak: 0,
    })),
  )
  const objectivesMetric = objectives
    ? (() => {
        const t = teams.find((x) => x.teamId === objectives.teamId)!
        return `${t.objectivesDone} objectif${t.objectivesDone > 1 ? 's' : ''} terminé${t.objectivesDone > 1 ? 's' : ''}`
      })()
    : null

  // 4. Meilleur climat — plus haute humeur moyenne sur la fenêtre.
  const climateScored = teams.map((t) => {
    const recent = recentByTeam.get(t.teamId)!
    const avg =
      recent.length > 0
        ? recent.reduce((s, p) => s + p.mood_score, 0) / recent.length
        : 0
    return {
      teamId: t.teamId,
      teamName: t.teamName,
      score: avg,
      tiebreak: recent.length,
    }
  })
  const climate = pickWinner(climateScored)
  const climateMetric = climate
    ? (() => {
        const s = climateScored.find((x) => x.teamId === climate.teamId)!
        return `${s.score.toFixed(1).replace('.', ',')}/5 d’humeur`
      })()
    : null

  return [
    {
      key: 'assiduity',
      label: 'Les plus assidus',
      description: 'Soumet son pulse mois après mois',
      winner: assiduity,
      metricLabel: assiduityMetric,
    },
    {
      key: 'communication',
      label: 'Meilleurs communicateurs',
      description: 'Partage l’info la plus riche sur Pulse',
      winner: communication,
      metricLabel: communicationMetric,
    },
    {
      key: 'objectives',
      label: 'Champions des objectifs',
      description: 'Le plus d’objectifs menés à terme',
      winner: objectives,
      metricLabel: objectivesMetric,
    },
    {
      key: 'climate',
      label: 'Meilleur climat',
      description: 'L’humeur d’équipe la plus positive',
      winner: climate,
      metricLabel: climateMetric,
    },
  ]
}
